<script setup lang="ts">
import { Trash2 } from 'lucide-vue-next'
import { computed } from 'vue'

import ReportDateValue from '@/components/reports/ReportDateValue.vue'
import ReportValueLookup from '@/components/reports/ReportValueLookup.vue'
import ReportValueMultiSelect from '@/components/reports/ReportValueMultiSelect.vue'
import ReportValueSuggest from '@/components/reports/ReportValueSuggest.vue'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { type Operator, type RegistryEntity, type RegistryField, type RuleCondition, allFields } from '@/types/reports'

const props = defineProps<{
  condition: RuleCondition
  entity: RegistryEntity
}>()

const emit = defineEmits<{
  'update:condition': [RuleCondition]
  remove: []
}>()

const OPERATOR_LABELS: Record<Operator, string> = {
  equals: 'is',
  not_equals: 'is not',
  in: 'is any of',
  not_in: 'is not any of',
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  between: 'between',
  contains: 'contains',
  starts_with: 'starts with',
  is_null: 'is empty',
}

const fieldOptions = computed(() => allFields(props.entity))

function filterableFields(fields: RegistryField[]): RegistryField[] {
  return fields.filter((f) => f.filterable)
}

const filterableRelations = computed(() =>
  Object.entries(props.entity.relations).filter(([, rel]) => filterableFields(rel.fields).length > 0),
)

const selectedField = computed<RegistryField | null>(
  () => fieldOptions.value.find((f) => f.path === props.condition.field)?.field ?? null,
)

const usesRecords = computed(() => selectedField.value?.options?.source === 'records')
const usesValueSet = computed(() => usesRecords.value || !!selectedField.value?.values?.length)
const usesSuggestions = computed(() => selectedField.value?.options?.source === 'values')

function patch(changes: Partial<RuleCondition>) {
  emit('update:condition', { ...props.condition, ...changes })
}

function onFieldChange(path: string) {
  patch({ field: path, operator: null, value: null })
}

function onOperatorChange(operator: Operator) {
  patch({ operator, value: operator === 'in' ? [] : operator === 'between' ? [null, null] : null })
}

function isDateType(type: string) {
  return type === 'date' || type === 'datetime'
}

function isNumericType(type: string) {
  return type === 'integer' || type === 'decimal'
}

const NEGATIVE_OPERATORS: Operator[] = ['not_equals', 'not_in']

type Polarity = 'positive' | 'negative'

const polarity = computed<Polarity>(() =>
  NEGATIVE_OPERATORS.includes(props.condition.operator!) ? 'negative' : 'positive',
)

const selectedValues = computed<string[]>(() => {
  const value = props.condition.value
  if (Array.isArray(value)) return value as string[]
  return value === null || value === undefined || value === '' ? [] : [String(value)]
})

function operatorFor(count: number, sign: Polarity): Operator {
  if (sign === 'negative') return count > 1 ? 'not_in' : 'not_equals'
  return count > 1 ? 'in' : 'equals'
}

function setSelectedValues(values: string[], sign = polarity.value) {
  patch({
    operator: operatorFor(values.length, sign),
    value: values.length > 1 ? values : (values[0] ?? null),
  })
}

function onPolarityChange(sign: string) {
  setSelectedValues(selectedValues.value, sign as Polarity)
}

const betweenValue = computed({
  get: (): [unknown, unknown] =>
    Array.isArray(props.condition.value) ? (props.condition.value as [unknown, unknown]) : [null, null],
  set: (val: [unknown, unknown]) => patch({ value: val }),
})
</script>

<template>
  <div class="bg-muted/30 flex flex-wrap items-start gap-2 rounded-md border p-2">
    <Select :model-value="condition.field ?? undefined" @update:model-value="(v) => onFieldChange(v as string)">
      <SelectTrigger class="w-48">
        <SelectValue placeholder="Select field…" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>{{ entity.label }}</SelectLabel>
          <SelectItem v-for="f in filterableFields(entity.fields)" :key="f.key" :value="f.key">{{
            f.label
          }}</SelectItem>
        </SelectGroup>
        <SelectGroup v-for="[relKey, rel] in filterableRelations" :key="relKey">
          <SelectLabel>{{ rel.label }}</SelectLabel>
          <SelectItem
            v-for="f in filterableFields(rel.fields)"
            :key="`${relKey}.${f.key}`"
            :value="`${relKey}.${f.key}`"
          >
            {{ f.label === rel.label ? f.label : `${rel.label}: ${f.label}` }}
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>

    <template v-if="selectedField && usesValueSet">
      <Select :model-value="polarity" @update:model-value="(v) => onPolarityChange(v as string)">
        <SelectTrigger class="w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="positive">is</SelectItem>
          <SelectItem value="negative">is not</SelectItem>
        </SelectContent>
      </Select>
      <span v-if="selectedValues.length > 1" class="text-muted-foreground self-center text-xs">
        {{ OPERATOR_LABELS[operatorFor(selectedValues.length, polarity)] }}
      </span>
    </template>

    <ReportDateValue
      v-else-if="selectedField && isDateType(selectedField.type)"
      :operator="condition.operator"
      :value="condition.value"
      @update="(next) => patch(next)"
    />

    <Select
      v-else-if="selectedField"
      :model-value="condition.operator ?? undefined"
      @update:model-value="(v) => onOperatorChange(v as Operator)"
    >
      <SelectTrigger class="w-40">
        <SelectValue placeholder="Operator…" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem v-for="op in selectedField.operators" :key="op" :value="op">{{ OPERATOR_LABELS[op] }}</SelectItem>
      </SelectContent>
    </Select>

    <ReportValueLookup
      v-if="selectedField && usesRecords"
      :base-entity="entity.key"
      :field="condition.field!"
      :entity-label="selectedField.label"
      :model-value="selectedValues"
      @update:model-value="(values) => setSelectedValues(values)"
    />

    <ReportValueMultiSelect
      v-else-if="selectedField && usesValueSet"
      :options="selectedField.values!"
      :model-value="selectedValues"
      @update:model-value="(values) => setSelectedValues(values)"
    />

    <template
      v-else-if="
        selectedField && !isDateType(selectedField.type) && condition.operator && condition.operator !== 'is_null'
      "
    >
      <template v-if="condition.operator === 'between'">
        <Input
          type="number"
          placeholder="From"
          class="w-28"
          :model-value="(betweenValue[0] as number) ?? ''"
          @update:model-value="(v) => (betweenValue = [v, betweenValue[1]])"
        />
        <span class="text-muted-foreground self-center text-sm">and</span>
        <Input
          type="number"
          placeholder="To"
          class="w-28"
          :model-value="(betweenValue[1] as number) ?? ''"
          @update:model-value="(v) => (betweenValue = [betweenValue[0], v])"
        />
      </template>

      <ReportValueSuggest
        v-else-if="usesSuggestions"
        :base-entity="entity.key"
        :field="condition.field!"
        :model-value="(condition.value as string) ?? ''"
        @update:model-value="(v) => patch({ value: v })"
      />

      <Select
        v-else-if="selectedField.type === 'boolean'"
        :model-value="(condition.value as string) ?? undefined"
        @update:model-value="(v) => patch({ value: v })"
      >
        <SelectTrigger class="w-28">
          <SelectValue placeholder="Value…" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">True</SelectItem>
          <SelectItem value="false">False</SelectItem>
        </SelectContent>
      </Select>

      <Input
        v-else
        :type="isNumericType(selectedField.type) ? 'number' : 'text'"
        placeholder="Value"
        class="w-40"
        :model-value="(condition.value as string | number) ?? ''"
        @update:model-value="(v) => patch({ value: v })"
      />
    </template>

    <button
      type="button"
      class="text-muted-foreground hover:text-destructive ml-auto self-center rounded p-1.5"
      aria-label="Remove condition"
      @click="emit('remove')"
    >
      <Trash2 class="size-4" />
    </button>
  </div>
</template>
